// MIDAS — HTML-entity decoding for bot chip-tag payloads (BUG 12, 2026-10-02).
//
// The bot's web formatter mints the inline chip tags (<plc-ref>, <live-ref>,
// <comp-ref>, <tel-ref>, <list-ref>, <page-ref>, <val>) with Python's
// `html.escape` — BOT_CAPABILITIES.md §12 parser notes:
//   • attribute values: html.escape(quote=True)  → & < > " '  become
//                       &amp; &lt; &gt; &quot; &#x27;
//   • inner text:       html.escape(quote=False) → & < >      become
//                       &amp; &lt; &gt;
// e.g. <plc-ref bit="DB10.&quot;NC&quot;.E_611Dready">DB10."NC".E_611Dready</plc-ref>
//
// The chip parser reads those values with a REGEX (not a DOM parser), so it
// receives the entity form verbatim. And because every value is then
// encodeURIComponent'd into a sentinel, ReactMarkdown's own entity decoding
// never sees it either — without this step `&quot;` reaches the chip label as
// literal text. Callers decode ONCE, at parse time (readAttr / inner-label
// capture in markdownComponentsV3.tsx), so the display label, tooltip, aria
// text, data attributes and click payloads all carry the real value.
//
// Why a small explicit map and not a DOM-based decoder:
//   • innerHTML-based decoding is an XSS sink — never.
//   • DOMParser text extraction is safe but parses MARKUP, so a raw `<` in a
//     value would be eaten as a tag (data loss), and it needs a DOM (this is a
//     pure parse layer, unit-tested without one).
//   • The producer is closed and known: Python html.escape emits exactly the
//     five entities above (single quote as the hex form &#x27;). We decode
//     those, the &apos; / &#39; spellings, and generic decimal/hex numeric
//     references. Unknown named entities are left verbatim (the producer never
//     emits them, and guessing is how double-decode bugs start).
//
// SINGLE PASS: one left-to-right `replace` over the input, and replaced output
// is never re-scanned — so `&amp;quot;` decodes to the TEXT `&quot;`, never to
// `"`. Calling this twice on the same value WOULD double-decode; that is why
// it runs at exactly one site per value.
//
// The decoded string may contain `<`, `>` or `"`. That is safe: every consumer
// renders it as a React text child / attribute value (React escapes both), and
// the code base forbids dangerouslySetInnerHTML (SEC-REQ-5).
//
// Pure — no React, no DOM.

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

// `&#NNN;` | `&#xHHH;` | `&name;` (the five names above only). Bounded digit
// runs keep the match linear and the code point parse small; out-of-range
// values are rejected in the callback, not by the regex.
const ENTITY_RE = /&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|(amp|lt|gt|quot|apos));/g;

const MAX_CODE_POINT = 0x10ffff;

const isDecodableCodePoint = (cp: number): boolean =>
  Number.isInteger(cp) &&
  cp > 0 && // NUL is never a legitimate payload character
  cp <= MAX_CODE_POINT &&
  !(cp >= 0xd800 && cp <= 0xdfff); // lone surrogates are not characters

/**
 * Decode the HTML entities the bot's `html.escape` produces, in ONE pass.
 * Strings without `&` are returned unchanged (fast path). Malformed or
 * out-of-range references are left verbatim rather than guessed at.
 */
export const decodeHtmlEntities = (value: string): string => {
  if (!value || value.indexOf("&") === -1) return value;
  return value.replace(
    ENTITY_RE,
    (whole: string, dec?: string, hex?: string, name?: string) => {
      if (name) return NAMED_ENTITIES[name];
      const cp = dec !== undefined ? parseInt(dec, 10) : parseInt(hex || "", 16);
      return isDecodableCodePoint(cp) ? String.fromCodePoint(cp) : whole;
    }
  );
};
